import React, { useMemo } from 'react';

import sort from 'utils/sort';
import { StringKeys, Filter } from './types';
import filter from './Filters/filterMap';
import BaseTable, { FilterStateObj, SortStateObj, BaseTableProps } from './BaseTable';

const Table = <T, K extends StringKeys<T>[]>(props: BaseTableProps<T, K>) => {
  const {data, filters = {}, ...rest} = props;
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
  const [sortState, setSortState] = React.useState<SortStateObj<T,K>>({});
  const [filterState, setFilterState] = React.useState<FilterStateObj<T,K>>({});
  
  const filteredData = useMemo<T[]>(() => {
    const filterArr = Object.entries<Filter>(filters);

    return data.filter(item => (
      filterArr.every(([h, f]) => !filterState[h] || filter[f.type](filters[h].transformation ? filters[h].transformation(filterState[h]):filterState[h], item[h] as unknown as never)) 
    ))
  }, [data, filters, filterState]);

  const finalData = useMemo<T[]>(() => {
    const sortedData = sortState.key && sortState.state !== 'none' ? filteredData.sort((v1, v2) => {
      const a = v1[sortState.key];
      const b = v2[sortState.key];
      const state = sortState.state;
      
      return sort(a,b, state as 'asc' | 'desc');
    }) : filteredData;

    return sortedData.slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage)
  }, [page, rowsPerPage, sortState, filteredData]);

  return <BaseTable
    data={finalData}
    filterStateMap={filterState}
    filters={filters}
    page={page}
    rowsPerPage={rowsPerPage}
    setFilterStateMap={setFilterState}
    setPage={setPage}
    setRowsPerPage={setRowsPerPage}
    setSortState={setSortState}
    sortState={sortState}
    total={filteredData.length}
    {...rest} 
  />
};

// React.memo gets rid of generics and thus causes ts compiler errors
// This forces memo type to be coherent
const typedMemo: <T>(c: T) => T = React.memo;

export default typedMemo(Table);
